import { useEffect, useState, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Button, Input, Typography } from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { cleanWasteCode } from 'admin/lib/CleaningRules'
import * as Rules from 'admin/lib/ValidationRules'
import {
  AutoCompleteFormItem,
  FormItem,
  FormRowWithValidation,
  StyledSection
} from 'admin/lib/Forms'

import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import { Undo } from 'admin/lib/Icons/Undo'

import {
  IMPERIAL_VOLUME_UNITS,
  IMPERIAL_WEIGHT_UNITS,
  METRIC_VOLUME_UNITS,
  METRIC_WEIGHT_UNITS
} from 'admin/lib/Records'
import { Waste as fields } from 'admin/lib/Records/fields'

import {
  CheckQuantity,
  CheckVolume,
  CheckWeight
} from 'admin/lib/SimpleDataValidation'
import {
  AddMultipleLineItemsModal,
  UnitSelector,
  useLineItemShortcuts
} from 'admin/pages/RecordEdit/common'
import { RAW, ENRICHED } from 'admin/pages/RecordEdit/common/constants'
import { useLineItemStore } from 'admin/pages/RecordEdit/LineItemStore'
import {
  returnDefaultValueForLineItemField,
  returnValueForLineItemField
} from 'admin/pages/RecordEdit/common/returnValueForLineItemField'
import { KnownContainerTypePresetSelect } from 'admin/pages/RecordEdit/Waste/KnownContainerTypePresetSelect'

import {
  isDisabled,
  isHidden
} from 'admin/pages/RecordEdit/common/rawEnrichedHelpers'
import { LineItemTitle } from 'admin/pages/RecordEdit/common/styledComponents'

export function WasteLineItem({
  trigger,
  index,
  item,
  rawItem,
  onDuplicate,
  onDuplicateMultiple,
  onRemove,
  setLineItemErrors,
  isImperial,
  dataView,
  showRaw,
  setEnrichedDisabled,
  setHasUnsavedLineItemChanges,
  saving
}) {
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [productDetailsOpen, setProductDetailsOpen] = useState(true)
  const [isDuplicateItemsMultipleOpen, setIsDuplicateItemsMultipleOpen] =
    useState(false)
  const isInitializedRef = useRef(false)

  const lineItem = useLineItemStore(state => state[index])
  const deleteLineItem = useLineItemStore(state => state.deleteKey)
  const duplicateLineItem = useLineItemStore(state => state.duplicateKey)
  const { moduleName, recordId } = useParams()

  const namePrefix = `uniquePrefix[${index}].`
  const rawNamePrefix = `uniquePrefix[${index}].raw_`

  // Alias Used for autocomplete fields
  const aliasPrefix = `enriched.items[${index}].`
  const rawAliasPrefix = `raw.items[${index}].`

  const form = useForm({
    reValidateMode: 'onChange',
    mode: 'all'
  })

  const [name, setName] = useState(item.containerType)

  const { TextArea } = Input

  const handleWasteCodeChange = event => {
    const updatedValue = cleanWasteCode(event.target.value)
    form.setValue(event.target.name, updatedValue)

    const updatedEvent = { ...event }
    updatedEvent.target.value = updatedValue
    dataChange(event)
  }

  const dataChange = event => {
    setUnsavedChanges(true)
    // Only notify parent after initial load is complete
    if (isInitializedRef.current) {
      setHasUnsavedLineItemChanges(true)
    }
    if (event?.target?.name) {
      form.trigger(event.target.name)
    }
    setLineItemErrors(form.errors, index)

    // Handle multiple events (e.g. for select fields that update other fields)
    if (Array.isArray(event)) {
      const newLineItem = { ...lineItem }
      event.forEach(eventPart => {
        const lineItemKey = eventPart.target.name.split(namePrefix).pop()
        newLineItem[lineItemKey] = eventPart.target.value

        if (
          lineItemKey.startsWith('raw_') &&
          lineItem[lineItemKey] !== eventPart.target.value
        ) {
          setEnrichedDisabled(true)
        }
      })

      useLineItemStore.setState({ [index]: newLineItem })
    } else {
      const lineItemKey = event.target.name.split(namePrefix).pop()

      if (
        lineItemKey.startsWith('raw_') &&
        lineItem[lineItemKey] !== event.target.value
      ) {
        setEnrichedDisabled(true)
      }

      if (lineItemKey === 'containerType') {
        setName(event.target.value)
      }

      const newLineItem = {
        ...lineItem,
        [lineItemKey]:
          event.target.type === 'number'
            ? parseFloat(event.target.value)
            : event.target.value
      }

      useLineItemStore.setState({ [index]: newLineItem })

      // If UoM change also trigger a blur event for the parent input
      // so that we trigger any validation rules
      if (
        lineItemKey.includes('UoM') &&
        lineItem[lineItemKey] !== event.target.value
      ) {
        const selector = `[name='${event.target.name.split('UoM')[0]}']`
        setTimeout(() => {
          document.querySelector(selector).blur()
          document.querySelector(selector).focus()
        }, 10)
      }
    }
  }

  const handleFocus = event => {
    const clipboardValue = sessionStorage.getItem('clipboard')
    sessionStorage.removeItem('clipboard')

    if (clipboardValue && clipboardValue !== '') {
      const updatedValue = returnValueForLineItemField(
        clipboardValue,
        event.target.type,
        event.target.value
      )
      form.setValue(event.target.name, updatedValue)

      const updatedEvent = { ...event }
      updatedEvent.target.value = updatedValue
      dataChange(updatedEvent)
    }
  }

  const openDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(true)
  }

  const closeDuplicateItemMultipleModal = e => {
    setIsDuplicateItemsMultipleOpen(false)
  }

  const duplicate = event => {
    // Duplicate on state
    duplicateLineItem(index)
    // Duplicate on Parent
    onDuplicate(lineItem)
  }

  const duplicateMultiple = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        // Duplicate on state
        duplicateLineItem(index)
      }
      onDuplicateMultiple(numOfNewItems, lineItem)
    }
  }

  const handleDuplicate = HandleSingleAndDoubleClick(
    duplicate,
    openDuplicateItemMultipleModal
  )

  const handleRemove = event => {
    // if lineitem has id (which means it has been saved) just change the isDeleted flag
    // Otherwise remove the entire item
    if (lineItem.id) {
      // Create events for Raw and Data and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: true
        }
      }
      const secondMockEvent = {
        target: {
          name: rawNamePrefix + 'isDeleted',
          value: true
        }
      }
      dataChange([mockEvent, secondMockEvent])
    } else {
      // Remove from parent
      onRemove()
      // Remove from state
      deleteLineItem(index)
    }
  }

  const handleUndoRemove = event => {
    // Only shown to deleted products that have a real id
    if (lineItem.id) {
      // Create events for Raw and Data and pass to dataChange
      const mockEvent = {
        target: {
          name: namePrefix + 'isDeleted',
          value: false
        }
      }
      const secondMockEvent = {
        target: {
          name: rawNamePrefix + 'isDeleted',
          value: false
        }
      }
      dataChange([mockEvent, secondMockEvent])
    }
  }

  const handleWheelScrollInNumberField = event => {
    if (
      document.activeElement.type === 'number' &&
      document.activeElement.matches(':hover')
    ) {
      event.preventDefault()
    }
  }

  const handleKnownContainerTypeSelectChange = value => {
    // Create mock event and pass to dataChange function
    const mockEvent = {
      target: {
        name: namePrefix + fields.lineItems.knownContainerType.path,
        value: value
      }
    }
    dataChange(mockEvent)
  }

  const shortcuts = useLineItemShortcuts(handleRemove, duplicate)

  // Clear unsavedChanges when save completes
  const prevSavingRef = useRef(saving)
  useEffect(() => {
    if (prevSavingRef.current === true && saving === false) {
      setUnsavedChanges(false)
    }
    prevSavingRef.current = saving
  }, [saving])

  useEffect(() => {
    // Sync to store when data changes and we're not saving or editing
    // Don't sync during save operations
    if (!saving && !unsavedChanges && item && rawItem) {
      // Populate from record
      const rawAndEnhancedItem = { ...item }
      Object.entries(rawItem).forEach(entry => {
        const [key, value] = entry
        rawAndEnhancedItem[`raw_${key}`] = value
      })
      useLineItemStore.setState({ [index]: rawAndEnhancedItem })
    }
  }, [index, item, rawItem, saving, unsavedChanges])

  // Mark component as initialized after first render
  useEffect(() => {
    isInitializedRef.current = true
  }, [])

  useEffect(() => {
    document.addEventListener('wheel', handleWheelScrollInNumberField, {
      passive: false
    })
  }, [])

  const handleProductOpenChange = () => {
    setProductDetailsOpen(!productDetailsOpen)
    sessionStorage.setItem(`${recordId}-${index}-open`, !productDetailsOpen)
  }

  useEffect(() => {
    const open = sessionStorage.getItem(`${recordId}-${index}-open`)
    if (open) {
      setProductDetailsOpen(open === 'true')
    }
    // check for changes to trigger value by adding to dependency array
  }, [index, recordId, trigger])

  if (!lineItem) {
    return null
  }

  if (lineItem.isDeleted) {
    return (
      <FormProvider {...form}>
        <StyledSection
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <LineItemTitle>
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Item'}
                </span>
                <br />
                Deleted
              </Typography.Text>

              <Button
                type="text"
                tabIndex={-1}
                icon={<Undo />}
                onClick={handleUndoRemove}
                disabled={dataView === ENRICHED}
                title={
                  dataView === ENRICHED
                    ? 'Undo delete is disabled in Enriched view'
                    : 'Undo delete'
                }
              />
            </LineItemTitle>
          )}
        </StyledSection>
      </FormProvider>
    )
  }

  if (lineItem.isRemoved) {
    return (
      <FormProvider {...form}>
        <StyledSection
          className={'LineItem'}
          onKeyDown={shortcuts.onKeyDown}
          colour="alt"
        >
          {({ firstInputRef }) => (
            <LineItemTitle>
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{
                  width: '272px',
                  paddingLeft: '1rem',
                  textIndent: '-1rem'
                }}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {name || 'New Item'}
                </span>
                <br />
                Removed by client
              </Typography.Text>
            </LineItemTitle>
          )}
        </StyledSection>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...form}>
      <StyledSection
        className={'LineItem'}
        onKeyDown={shortcuts.onKeyDown}
        colour="alt"
      >
        {({ firstInputRef }) => (
          <details
            {...(productDetailsOpen ? { open: true } : {})}
            className="LineItemDetails"
            style={{ position: 'relative' }}
          >
            <LineItemTitle
              onClick={e => {
                // prevent default on the Click Event to stop it conflicting
                e.preventDefault()
              }}
            >
              <Typography.Text
                strong
                type="secondary"
                ellipsis={true}
                style={{ width: '272px' }}
                onClick={handleProductOpenChange}
              >
                <Typography.Text strong>{index + 1}:</Typography.Text>{' '}
                {name || 'New Item'}
              </Typography.Text>

              <ButtonGroup>
                {dataView === RAW && (
                  <>
                    <Button
                      type="text"
                      tabIndex="-1"
                      icon={<Icons.CopyOutlined />}
                      onClick={handleDuplicate}
                      title="Duplicate"
                    />
                    <Button
                      type="text"
                      tabIndex="-1"
                      danger
                      icon={<Icons.DeleteOutlined />}
                      onClick={handleRemove}
                      title="Delete"
                    />
                  </>
                )}
                {productDetailsOpen && (
                  <UpCircleFilledInline
                    title="Click to collapse"
                    className="productCollapseIcon"
                    onClick={handleProductOpenChange}
                  />
                )}
                {!productDetailsOpen && (
                  <DownCircleOutlinedInline
                    title="Click to expand"
                    onClick={handleProductOpenChange}
                  />
                )}
              </ButtonGroup>
            </LineItemTitle>

            <FormItem
              name={rawNamePrefix + fields.rawLineItems.id.path}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.id,
                lineItem['raw_id']
              )}
              style={{ display: 'none' }}
            >
              <Input type="hidden" />
            </FormItem>
            <FormItem
              name={namePrefix + fields.lineItems.id.path}
              defaultValue={returnDefaultValueForLineItemField(
                item.id,
                lineItem.id
              )}
              style={{ display: 'none' }}
            >
              <Input type="hidden" />
            </FormItem>

            <FormItem
              label={fields.rawLineItems.containerType.label}
              name={rawNamePrefix + fields.rawLineItems.containerType.path}
              alias={aliasPrefix + fields.rawLineItems.containerType.path}
              inputRef={dataView === RAW ? firstInputRef : null}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.containerType,
                lineItem['raw_containerType']
              )}
              onBlur={dataChange}
              onChange={dataChange}
              onFocus={handleFocus}
              children={<TextArea autoSize />}
              // Always show as disabled in Enriched
              disabled={isDisabled(true, dataView, true)}
              hidden={false}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.lineItems.containerType.label}
              name={namePrefix + fields.lineItems.containerType.path}
              alias={aliasPrefix + fields.lineItems.containerType.path}
              defaultValue={returnDefaultValueForLineItemField(
                item.containerType,
                lineItem.containerType
              )}
              children={<TextArea autoSize />}
              id={`wasteLineItemContainerType_${index}`}
              disabled={isDisabled(
                fields.lineItems.containerType.isRawField,
                dataView,
                showRaw
              )}
              hidden={true}
            />

            <KnownContainerTypePresetSelect
              onChange={handleKnownContainerTypeSelectChange}
              namePrefix={namePrefix}
              index={index}
              defaultValue={returnDefaultValueForLineItemField(
                item.knownContainerType,
                lineItem.knownContainerType
              )}
              disabled={
                lineItem.knownContainerType === '' ||
                isDisabled(
                  fields.lineItems.knownContainerType.isRawField,
                  dataView,
                  showRaw
                )
              }
              hidden={isHidden(
                fields.lineItems.knownContainerType.isRawField,
                dataView,
                showRaw
              )}
              instrumented={true}
              containerType={lineItem.containerType}
            />

            <FormItem
              label={fields.rawLineItems.wasteDescription.label}
              name={rawNamePrefix + fields.rawLineItems.wasteDescription.path}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.wasteDescription,
                lineItem['raw_wasteDescription']
              )}
              onBlur={dataChange}
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(
                fields.rawLineItems.wasteDescription.isRawField,
                dataView,
                showRaw
              )}
              hidden={isHidden(true, dataView, showRaw)}
              instrumented={true}
            >
              <Input type="string" />
            </FormItem>

            <FormItem
              label={fields.lineItems.wasteDescription.label}
              name={namePrefix + fields.lineItems.wasteDescription.path}
              inputRef={dataView !== RAW ? firstInputRef : null}
              defaultValue={returnDefaultValueForLineItemField(
                item.wasteDescription,
                lineItem.wasteDescription
              )}
              id={`wasteLineItemWasteDescription_${index}`}
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(
                fields.lineItems.wasteDescription.isRawField,
                dataView,
                showRaw
              )}
              hidden={isHidden(
                fields.lineItems.wasteDescription.isRawField,
                dataView,
                showRaw
              )}
            />

            <FormItem
              label={fields.rawLineItems.wasteCode.label}
              name={rawNamePrefix + fields.rawLineItems.wasteCode.path}
              alias={rawAliasPrefix + fields.rawLineItems.wasteCode.path}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.wasteCode,
                lineItem['raw_wasteCode']
              )}
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(true, dataView, showRaw)}
              hidden={isHidden(true, dataView, showRaw)}
              instrumented={true}
            />
            <AutoCompleteFormItem
              label={fields.lineItems.wasteCode.label}
              name={namePrefix + fields.lineItems.wasteCode.path}
              alias={aliasPrefix + fields.lineItems.wasteCode.path}
              defaultValue={returnDefaultValueForLineItemField(
                item.wasteCode,
                lineItem.wasteCode
              )}
              onBlur={handleWasteCodeChange}
              onChange={dataChange}
              onFocus={handleFocus}
              rules={Rules.ValidEWCCode}
              id={`wasteLineItemWasteCode_${index}`}
              disabled={isDisabled(
                fields.lineItems.wasteCode.isRawField,
                dataView,
                showRaw
              )}
              hidden={isHidden(
                fields.lineItems.wasteCode.isRawField,
                dataView,
                showRaw
              )}
              instrumented={true}
            />

            <FormRowWithValidation>
              <FormItem
                label={fields.lineItems.volumeEstimated.label}
                name={namePrefix + fields.lineItems.volumeEstimated.path}
                defaultValue={returnDefaultValueForLineItemField(
                  item.volumeEstimated,
                  lineItem.volumeEstimated
                )}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemEstVol_${index}`}
                disabled={true}
                hidden={isHidden(
                  fields.lineItems.volumeEstimated.isRawField,
                  dataView,
                  showRaw
                )}
                instrumented={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={!isImperial ? 'm3' : 'cu yd'}
                />
              </FormItem>

              <FormItem
                label={fields.lineItems.weightEstimated.label}
                name={namePrefix + fields.lineItems.weightEstimated.path}
                defaultValue={returnDefaultValueForLineItemField(
                  item.weightEstimated,
                  lineItem.weightEstimated
                )}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemEstWeight_${index}`}
                disabled={true}
                hidden={isHidden(
                  fields.lineItems.weightEstimated.isRawField,
                  dataView,
                  showRaw
                )}
                instrumented={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={!isImperial ? 't' : 'lbs'}
                />
              </FormItem>
            </FormRowWithValidation>

            <FormRowWithValidation>
              <FormItem
                label={fields.rawLineItems.volume.label}
                name={rawNamePrefix + fields.rawLineItems.volume.path}
                defaultValue={returnDefaultValueForLineItemField(
                  rawItem.volume,
                  lineItem['raw_volume']
                )}
                onChange={dataChange}
                onFocus={handleFocus}
                disabled={isDisabled(true, dataView, showRaw)}
                hidden={isHidden(true, dataView, showRaw)}
                instrumented={true}
              />
              <FormItem
                label={fields.rawLineItems.weight.label}
                name={rawNamePrefix + fields.rawLineItems.weight.path}
                defaultValue={returnDefaultValueForLineItemField(
                  rawItem.weight,
                  lineItem['raw_weight']
                )}
                onChange={dataChange}
                onFocus={handleFocus}
                disabled={isDisabled(true, dataView, showRaw)}
                hidden={isHidden(true, dataView, showRaw)}
                instrumented={true}
              />
            </FormRowWithValidation>
            <FormRowWithValidation>
              <FormItem
                label={fields.lineItems.volume.label}
                name={namePrefix + fields.lineItems.volume.path}
                defaultValue={returnDefaultValueForLineItemField(
                  item.volume,
                  lineItem.volume
                )}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemVol_${index}`}
                disabled={isDisabled(
                  fields.lineItems.volume.isRawField,
                  dataView,
                  showRaw
                )}
                hidden={isHidden(
                  fields.lineItems.volume.isRawField,
                  dataView,
                  showRaw
                )}
                simpleDataValidationRules={v =>
                  CheckVolume(v, lineItem.volumeUoM)
                }
                instrumented={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={namePrefix + fields.lineItems.volumeUoM.path}
                      currentValue={lineItem.volumeUoM}
                      allValues={
                        !isImperial
                          ? METRIC_VOLUME_UNITS
                          : IMPERIAL_VOLUME_UNITS
                      }
                      onChange={dataChange}
                      disabled={isDisabled(
                        fields.lineItems.volume.isRawField,
                        dataView,
                        showRaw
                      )}
                    />
                  }
                />
              </FormItem>

              <FormItem
                label={fields.lineItems.weight.label}
                name={namePrefix + fields.lineItems.weight.path}
                defaultValue={returnDefaultValueForLineItemField(
                  item.weight,
                  lineItem.weight
                )}
                rules={Rules.NonNegativeNumber}
                onChange={dataChange}
                onFocus={handleFocus}
                id={`wasteLineItemWeight_${index}`}
                disabled={isDisabled(
                  fields.lineItems.weight.isRawField,
                  dataView,
                  showRaw
                )}
                hidden={isHidden(
                  fields.lineItems.weight.isRawField,
                  dataView,
                  showRaw
                )}
                simpleDataValidationRules={v =>
                  CheckWeight(v, lineItem.weightUoM)
                }
                instrumented={true}
              >
                <Input
                  type="number"
                  min="0"
                  step="any"
                  suffix={
                    <UnitSelector
                      name={namePrefix + fields.lineItems.weightUoM.path}
                      currentValue={lineItem.weightUoM}
                      allValues={
                        !isImperial
                          ? METRIC_WEIGHT_UNITS
                          : IMPERIAL_WEIGHT_UNITS
                      }
                      onChange={dataChange}
                      disabled={isDisabled(
                        fields.lineItems.weight.isRawField,
                        dataView,
                        showRaw
                      )}
                    />
                  }
                />
              </FormItem>
            </FormRowWithValidation>

            <FormItem
              label={fields.rawLineItems.quantity.label}
              name={rawNamePrefix + fields.rawLineItems.quantity.path}
              defaultValue={returnDefaultValueForLineItemField(
                rawItem.quantity,
                lineItem['raw_quantity']
              )}
              onChange={dataChange}
              onFocus={handleFocus}
              disabled={isDisabled(true, dataView, showRaw)}
              hidden={isHidden(true, dataView, showRaw)}
              simpleDataValidationRules={v =>
                CheckQuantity(typeof v === 'number' ? v.toString() : v)
              }
              instrumented={true}
            />
            <FormItem
              label={fields.lineItems.quantity.label}
              name={namePrefix + fields.lineItems.quantity.path}
              defaultValue={
                // Additional check because quantity defaults to 1
                item.quantity === 1 && lineItem.quantity
                  ? lineItem.quantity
                  : item.quantity
                    ? item.quantity
                    : lineItem.quantity
              }
              rules={Rules.MandatoryNumberGreaterThanZero}
              onChange={dataChange}
              onFocus={handleFocus}
              id={`wasteLineItemQuantity_${index}`}
              disabled={isDisabled(
                fields.lineItems.quantity.isRawField,
                dataView,
                showRaw
              )}
              hidden={isHidden(
                fields.lineItems.quantity.isRawField,
                dataView,
                showRaw
              )}
              simpleDataValidationRules={v =>
                CheckQuantity(typeof v === 'number' ? v.toString() : v)
              }
              instrumented={true}
            >
              <Input type="number" min="0" step="any" />
            </FormItem>
          </details>
        )}
      </StyledSection>
      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isDuplicateItemsMultipleOpen}
        onClose={closeDuplicateItemMultipleModal}
        onSubmit={val => {
          duplicateMultiple(val)
        }}
        duplicateLineItemName={item.containerType}
      />
    </FormProvider>
  )
}

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
`
export const UpCircleFilledInline = styled(Icons.UpCircleFilled)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
export const DownCircleOutlinedInline = styled(Icons.DownCircleOutlined)`
  font-size: 20px;
  color: ${colours.TEAL};
  margin-left: 0.25rem;
`
